import * as z from 'zod';
import isObject from 'lodash/isObject';
import debug from 'debug';
import { isDOMEvent, isComponentContext, assertVueComponent } from '../helpers';
const log = debug('itp:core:actions');
class ConditionalAction {
    when;
    confirm;
    constructor(when, confirm) {
        this.when = when;
        this.confirm = confirm;
    }
    get fullName() {
        return this.constructor.name;
    }
    async isExecutionPrevented(event, args) {
        let result = false;
        if (this.when) {
            log('[%s] evaluating when expression - event: %o - args: %o', this.fullName, event, args);
            result = (await this.when(event, args)) !== true;
        }
        if (!result && this.confirm) {
            const confirm = await this.confirm(event, args);
            log('[%s] asking user confirmation - %o', this.fullName, confirm);
            assertVueComponent(event.component);
            result = (await event.component.$root.$imodal.confirm(confirm)) !== true;
        }
        if (result) {
            log('[%s] execution is prevented.', this.fullName);
            return result;
        }
        return result;
    }
}
export class Action extends ConditionalAction {
    actionArgs;
    events;
    displayName;
    currentEvent;
    constructor(args) {
        super(args ? args.when : undefined, args ? args.confirm : undefined);
        this.actionArgs = args ? args.actionArgs : undefined;
        this.events = args ? args.events : undefined;
        this.displayName = args ? args.displayName : undefined;
        this.currentEvent = null;
    }
    static get args_schema() {
        return {
            name: z.literal('foo'),
            args: z.object({})
        };
    }
    get isRunning() {
        return !!this.currentEvent;
    }
    get fullName() {
        const cname = this.constructor.name;
        return this.displayName ? `${cname}[${this.displayName}]` : cname;
    }
    async trigger(component) {
        if (this.currentEvent) {
            throw new Error('Action already running.');
        }
        try {
            const event = new Event('x-custom');
            const ev = { originalEvent: event, component, scope: {}, data: null };
            if (await this.isExecutionPrevented(ev, {})) {
                return null;
            }
            const args = this.actionArgs
                ? await this.actionArgs(ev)
                : null;
            this.currentEvent = ev;
            log('[%s] TRIGGER_FROM_CUSTOM - event: %o - args: %o (%o)', this.fullName, event, args, this);
            return await this.handle(ev, args);
        }
        catch (err) {
            console.error(err);
        }
        finally {
            this.currentEvent = null;
        }
    }
    async triggerFromDOM(component, event, scope) {
        if (this.currentEvent) {
            throw new Error('Action already running.');
        }
        try {
            let data = {};
            if (!isDOMEvent(event) && isObject(event)) {
                data = event;
            }
            const ev = { originalEvent: event, component, scope, data };
            if (await this.isExecutionPrevented(ev, {})) {
                return null;
            }
            const args = this.actionArgs
                ? await this.actionArgs(ev)
                : null;
            this.currentEvent = ev;
            log('[%s] TRIGGER_FROM_DOM - event: %o - args: %o (%o)', this.fullName, event, args, this);
            return await this.handle(ev, args);
        }
        finally {
            this.currentEvent = null;
        }
    }
    async triggerFromParent(parent, data) {
        if (this.currentEvent) {
            throw new Error('Action already running.');
        }
        try {
            const ev = {
                originalEvent: parent.originalEvent,
                component: parent.component,
                scope: parent.scope,
                parent,
                data
            };
            if (await this.isExecutionPrevented(ev, {})) {
                return null;
            }
            const args = this.actionArgs
                ? await this.actionArgs(ev)
                : null;
            this.currentEvent = ev;
            log('TRIGGER_FROM_PARENT [%s] args: %o (%o)', this.fullName, args, this);
            return await this.handle(ev, args);
        }
        finally {
            this.currentEvent = null;
        }
    }
    async dispatch(event, args) {
        if (!this.events || !this.events.length) {
            log('DISPATCH %s [%s] Skipping, no subscribers. args=%o (action=%o)', event, this.fullName, args, this);
            return;
        }
        if (!this.currentEvent) {
            throw new Error(`Cannot dispatch ${event}: missing current event.`);
        }
        const data = isObject(args)
            ? { ...this.currentEvent.data, ...args }
            : this.currentEvent.data;
        const ev = { ...this.currentEvent, data };
        log('DISPATCH %s [%s] - event: %o, args: %o', event, this.fullName, ev, args);
        for (let handler of this.events) {
            if (handler.event == event) {
                await handler.execute(ev, args);
            }
        }
    }
}
export class EventHandlerImpl extends ConditionalAction {
    event;
    ctx;
    action;
    disabled;
    displayName;
    constructor(options) {
        super(options.when, options.confirm);
        this.event = options.event;
        this.ctx = options.component;
        this.action = options.action;
        this.disabled = options.disabled;
        this.displayName = options ? options.displayName : '';
    }
    get fullName() {
        let prefix = '';
        if (this.ctx && this.ctx.$uid) {
            prefix = this.ctx.$uid + '/';
        }
        const cname = prefix + this.constructor.name + '[' + this.event + ']';
        return this.displayName ? `${cname}::${this.displayName}` : cname;
    }
    async execute(event, args) {
        if (this.disabled) {
            return;
        }
        if (await this.isExecutionPrevented(event, args)) {
            return null;
        }
        log('EXECUTE [%s] event: %o', this.fullName, event);
        log('EXECUTE [%s] args: %o', this.fullName, args);
        return await this.action.triggerFromParent(event, args);
    }
    async executeFromDOM(component, event, scope) {
        let data = {};
        if (!isDOMEvent(event) && isObject(event)) {
            data = event;
        }
        return await this.execute({
            component,
            originalEvent: event,
            scope,
            data
        }, data);
    }
}
export class EventHandlerGroup {
    event;
    ctx;
    handlers;
    displayName;
    constructor(options) {
        this.event = options.event;
        this.handlers = options.handlers;
        this.displayName = options ? options.displayName : '';
    }
    get fullName() {
        const cname = this.constructor.name + ':' + this.event;
        return this.displayName ? `${cname}#${this.displayName}` : cname;
    }
    async executeFromDOM(component, event, scope) {
        try {
            let data = {};
            if (!isDOMEvent(event) && isObject(event)) {
                data = event;
            }
            const ev = { component, originalEvent: event, scope, data };
            log('[%s] executeFromDOM - event: %o - args: %o', this.fullName, ev, data);
            for (const h of this.handlers) {
                await h.execute(ev, data);
            }
        }
        catch (error) {
            if (isComponentContext(component)) {
                component.handleError(error);
            }
        }
    }
}
//# sourceMappingURL=base.js.map